import Phaser from 'phaser';
import { t } from '@i18n/index';
import { formatPosition } from '@core/geo';
import { PALETTE, TEXT_COLOUR, FONT, readoutStyle, px } from '../theme';
import type { HudState } from './WorldMapScene';
import type { Campaign } from '@core/campaign/campaign';

/**
 * Anzeigen ueber der Weltkarte.
 *
 * Als eigene Szene, weil Objekte der Kartenszene zwangslaeufig mit der Kamera
 * skalieren — bei Zoomstufen zwischen 1 und 100 waere Text dort entweder
 * unlesbar klein oder bildschirmfuellend. Eine zweite Szene hat ihre eigene
 * Kamera und bleibt davon unberuehrt.
 */
export class HudScene extends Phaser.Scene {
  private rows = new Map<string, Phaser.GameObjects.Text>();
  private zoomLabel!: Phaser.GameObjects.Text;
  private hint!: Phaser.GameObjects.Text;
  /** Alles, was nur waehrend einer Fahrt zu sehen sein soll. */
  private panelObjects: (Phaser.GameObjects.Text | Phaser.GameObjects.Rectangle)[] = [];

  constructor() {
    super('Hud');
  }

  /** Siehe WorldMapScene.init(): Szenen-Instanzen werden wiederverwendet. */
  init(): void {
    this.rows = new Map();
    this.panelObjects = [];
  }

  /**
   * Blendet eine kurze Meldung am oberen Rand ein.
   *
   * Fuer neue Notrufe, die waehrend der Fahrt hereinkommen. Sie halten die
   * Fahrt nicht an — man kann ohnehin nicht umkehren —, aber man soll wissen,
   * dass anderswo jetzt auch eine Uhr laeuft.
   */
  /**
   * Zeigt oder verbirgt die Fahrtanzeige.
   *
   * Im Lagebild faehrt niemand, und dann stuenden hier sieben Zeilen mit
   * Gedankenstrichen. Die Einblendungen fuer neue und verstrichene Notrufe
   * bleiben davon unberuehrt — die gelten auch im Stillstand.
   */
  setPanelVisible(visible: boolean): void {
    for (const object of this.panelObjects) object.setVisible(visible);
  }

  announce(message: string, grave = false): void {
    const { width } = this.scale;

    /*
     * Zwei Toenungen.
     *
     * Ein neuer Notruf ist ein Alarm — rot, kurz, dringend. Ein verstrichener
     * ist kein Alarm mehr, sondern eine Nachricht, und die soll nicht schreien.
     * Sie steht laenger und in gedaempfter Schrift: Es gibt nichts mehr zu tun,
     * nur noch etwas zur Kenntnis zu nehmen.
     */
    const text = this.add
      .text(width / 2, 34, message, {
        ...readoutStyle(12),
        color: grave ? PALETTE.inkDim : TEXT_COLOUR.danger,
        backgroundColor: '#0a1119',
        padding: { x: 14, y: 8 },
        align: 'center',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    this.tweens.add({
      targets: text,
      alpha: 1,
      duration: 220,
      yoyo: true,
      hold: grave ? 5200 : 3200,
      onComplete: () => text.destroy(),
    });
  }

  create(): void {
    const { width, height } = this.scale;

    this.drawVignette(width, height);

    // Anzeigetafel unten links.
    const panelWidth = 330;
    const panelHeight = 168;
    const panelX = 16;
    const panelY = height - panelHeight - 16;

    const panel = this.add
      .rectangle(panelX, panelY, panelWidth, panelHeight, PALETTE.panel, 0.82)
      .setOrigin(0, 0);
    panel.setStrokeStyle(1, PALETTE.panelEdge);
    this.panelObjects.push(panel);

    const labels: [string, string][] = [
      ['position', t('map.hud.position')],
      ['depth', t('map.hud.depth')],
      ['speed', t('map.hud.speed')],
      ['remaining', t('map.hud.remaining')],
      ['fuel', t('voyage.fuel')],
      ['sea', t('map.hud.sea')],
      ['forecast', t('briefing.forecast')],
    ];

    labels.forEach(([key, label], index) => {
      const y = panelY + 13 + index * 21;
      this.panelObjects.push(
        this.add.text(panelX + 14, y, label, {
        fontFamily: FONT.mono,
        fontSize: px(11),
        color: PALETTE.inkFaint,
        }),
      );
      const value = this.add
        .text(panelX + panelWidth - 14, y, '—', {
          fontFamily: FONT.mono,
          fontSize: px(11),
          color: PALETTE.ink,
        })
        .setOrigin(1, 0);
      this.rows.set(key, value);
      this.panelObjects.push(value);
    });

    // Zoomstufe oben rechts.
    this.zoomLabel = this.add
      .text(width - 18, 18, '', readoutStyle(12, true))
      .setOrigin(1, 0);

    this.hint = this.add
      .text(18, 18, t('map.hint'), {
        fontFamily: FONT.mono,
        fontSize: px(11),
        color: PALETTE.inkFaint,
      })
      .setOrigin(0, 0);

    // Hinweis und Zoomstufe gehoeren ebenfalls zur Fahrt.
    this.panelObjects.push(this.hint, this.zoomLabel);

    /*
     * Sichtbarkeit selbst bestimmen, nicht von aussen setzen lassen.
     *
     * `scene.launch()` ist asynchron: Ruft die Kartenszene direkt danach
     * `setPanelVisible()`, ist `create()` hier noch nicht gelaufen und die
     * Liste leer — der Balken blieb stehen. Die Szene fragt deshalb selbst,
     * sobald sie steht.
     */
    const campaign = this.registry.get('campaign') as Campaign | undefined;
    this.setPanelVisible((campaign?.activeMissions.length ?? 0) > 0);

    const worldMap = this.scene.get('WorldMap');
    worldMap.events.on('hud', this.render, this);
    // Ohne dieses Aufräumen bliebe der Listener beim Szenenwechsel hängen.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      worldMap.events.off('hud', this.render, this);
    });
  }

  /**
   * Weiche Abdunklung zu den Raendern hin.
   *
   * Bindet den Blick in der Bildmitte und nimmt der grossen, gleichmaessig
   * hellen Kartenflaeche die Haerte. Als Farbverlauf auf einer eigenen Textur,
   * weil Phasers Graphics keine Verlaeufe zeichnet.
   */
  private drawVignette(width: number, height: number): void {
    const key = 'hud-vignette';
    if (this.textures.exists(key)) this.textures.remove(key);

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(2, Math.round(width / 4));
    canvas.height = Math.max(2, Math.round(height / 4));

    const context = canvas.getContext('2d');
    if (context === null) return;

    const gradient = context.createRadialGradient(
      canvas.width / 2,
      canvas.height / 2,
      Math.min(canvas.width, canvas.height) * 0.35,
      canvas.width / 2,
      canvas.height / 2,
      Math.max(canvas.width, canvas.height) * 0.75,
    );
    gradient.addColorStop(0, 'rgba(0,0,0,0)');
    gradient.addColorStop(1, 'rgba(0,0,0,0.55)');

    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);

    this.textures.addCanvas(key, canvas);
    this.add.image(0, 0, key).setOrigin(0, 0).setDisplaySize(width, height).setDepth(-1);
  }

  private render(state: HudState): void {
    this.rows.get('position')?.setText(formatPosition(state.position));

    this.rows
      .get('depth')
      ?.setText(
        state.aground
          ? t('map.hud.depth.aground')
          : t('map.hud.metres', { metres: Math.round(state.depthMetres) }),
      )
      .setColor(state.aground ? '#c9736f' : PALETTE.ink);

    this.rows
      .get('speed')
      ?.setText(
        state.underway ? t('map.hud.knots', { knots: Math.round(state.speedKnots) }) : '—',
      );

    this.rows
      .get('remaining')
      ?.setText(t('map.hud.distance', { miles: Math.round(state.remainingNm) }));

    this.rows.get('fuel')?.setText(String(Math.round(state.fuel)));
    this.rows.get('sea')?.setText(t(state.seaStateKey));

    // Die Vorschau ist der eigentliche Spannungsträger unterwegs: Sie kippt,
    // wenn das Wetter die Fahrt bremst.
    const forecast = this.rows.get('forecast');
    forecast?.setText(t(state.projectedTier));
    forecast?.setColor(
      state.projectedTier === 'outcome.rescue'
        ? '#8fd9c4'
        : state.projectedTier === 'outcome.nothing'
          ? '#c9736f'
          : '#e0b070',
    );

    this.zoomLabel.setText(`${t('map.hud.zoom')}: ${t(`map.zoom.${state.zoomLevel}`)}`);
    this.hint.setVisible(false);
  }
}
